import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  ListItem,
  useMediaQuery,
  Container,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from 'react-router-dom';
import { visuallyHidden } from '@mui/utils';

// import { useTheme } from '@emotion/react';
// import { useTheme } from '@mui/material/styles';
// import './CarouselAcConnex.scss';

// Replace old constants
const DESKTOP_CARD_WIDTH = 380;
const TABLET_CARD_WIDTH = 380;
const MOBILE_CARD_WIDTH = 272;

interface CardData {
  id: number;
  header: string;
  img: string;
  body: string;
  alt: string;
  button: string;
  url: string;
}
interface CarouselData {
  header: string;
  body: string;
  cards: Array<CardData>
}

interface FeatureCardProps extends CardData {
  cardWidth: number;
  isFirst?: boolean;
  headingId?: string;
  tabIndex?: number;
  'aria-hidden'?: boolean;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  position: any;
  total: number;
}

const FeatureCard = React.forwardRef<HTMLLIElement, FeatureCardProps>(({
  id, header, img, alt, body, cardWidth, button, url

}, ref) => {
  const parts = body.split(/(<strong>.*?<\/strong>)/g);
  const featureRefs = React.useRef<Array<HTMLLIElement | null>>([]);
  const isMobile = useMediaQuery('(max-width: 700px)');

  const focusFeature = (idx: number) => {
    requestAnimationFrame(() => featureRefs.current[idx]?.focus());
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && 3) {
      e.preventDefault();
      focusFeature(0);
    }
  };

  const navigate = useNavigate();
  const {t , i18n} = useTranslation();
  const data: any = t('ndc-program.home.connectionOptions', { returnObjects: true });

  const handleNavigate = (url) => {
    setTimeout(()=>{window.scrollTo({ top: 0, behavior: 'smooth' });},50);
    navigate(`/${i18n.language}${url}`);
    
  }

  const handleFeatureKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (idx + 1) < 3 ? idx + 1 : 0;
      focusFeature(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (idx - 1) >= 0 ? idx - 1 : 3 - 1;
      focusFeature(prev);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      (ref as React.RefObject<HTMLLIElement>)?.current?.focus();
    }
  };

  return (
    <ListItem
      ref={ref}
      component="li"
      onKeyDown={handleCardKeyDown}
      sx={{
        maxWidth:isMobile?MOBILE_CARD_WIDTH:DESKTOP_CARD_WIDTH,
        minHeight: 350,
        flexShrink: 0,
        borderRadius: '16px',
        padding: 0,
        backgroundColor: '#fff',
        boxSizing:'border-box',
        border: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        listStyle: 'none',
        boxShadow: '0 0 15px 0 rgba(0,0,0,0.15)',
        position: 'relative',
        '&:focus-visible': {
          outline: '3px solid #1460AA',
          outlineOffset: '4px'
        }
      }}
      disableGutters
    >

      {/* Features (not tabbable initially) */}
      <Box
        sx={{
          listStyle: 'none',
          height: '100%',
          margin: 0,
          paddingLeft: '0px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',

        }}>
        {/* Header */}
        <img src={img} alt={alt} style={{ width: '100%', display:'block',height: isMobile ? "140px" : "190px", marginBottom: 16, objectFit: 'cover', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }} />
        <Box
          className='card-content'
          sx={{
            padding: "0px 24px",
            alignSelf: 'flex-start',
            display: 'flex',
            minHeight: '160px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '16px'
          }}>
          <Box component="span" sx={visuallyHidden}>{`${data.label} ${id+1} of 3`}</Box>
          <Typography id={`card-heading-${id}`} variant="h3" sx={{
            // fontFamily: 'Open Sans', 
            fontWeight: 700, fontSize: '18px', lineHeight: '24px'
          }}>
            {header}
            {/* <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, p: 0, m: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            {`${position} of ${total}`}
          </Box> */}
          </Typography>
          <Box
            //key={f.id}
            ref={(el: HTMLLIElement | null) => { featureRefs.current[id] = el; }}
            aria-posinset={id + 1}
            aria-setsize={3}
            onKeyDown={(e) => handleFeatureKeyDown(e, id)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              outline: 'none',
              position: 'relative',
              '&:focus-visible': {
                outline: '2px solid #1460AA',
                borderRadius: '4px'
              }
            }}
          >

            <Typography variant="body2" sx={{
              color: '#101828',
              //  fontFamily: 'Open Sans', 
              fontWeight: 600, fontSize: '12px', lineHeight: '20px'
            }}>
              {parts.map((parts, index)=>{
                if(parts.startsWith('<strong>')){
                  const content = parts.replace(/<\/?strong>/g,'');
                  return <Box key={index} component='span' fontWeight='bold'>{content}</Box>
                }
                return parts;
              })}
            </Typography>
          </Box>

        </Box>
        <Button id={`card-button-${id}`} component='a' onClick={() => handleNavigate(url)} variant='text' aria-labelledby={`card-button-${id} card-heading-${id}`} sx={{ padding: "0px 24px 24px 24px", display: 'flex', alignItems: "center", justifyContent: "center", '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px' } }} endIcon={<ArrowForwardIosIcon></ArrowForwardIosIcon>}>{button}</Button>
      </Box>
    </ListItem>
  );
});

FeatureCard.displayName = 'FeatureCard';

const SingleCarousel: React.FC<{ carouselData: CarouselData }> = ({ carouselData }) => {
  const [startIndex, setStartIndex] = useState(0);
  // NEW drag states
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const headingId = `section-heading`;

  // Fallback accessible names if blank/empty

  const isMobile = useMediaQuery('(max-width: 700px)');
  const isTablet = useMediaQuery('(min-width:701px) and (max-width:1024px)');
  // const isLargeTab =useMediaQuery('(min-width:901px) and (max-width:1024px)');
  const VISIBLE_CARDS = isMobile ? 1.85 : isTablet ? 2.5 : 3;
  const cardWidth = isTablet ? TABLET_CARD_WIDTH : isMobile ? MOBILE_CARD_WIDTH : DESKTOP_CARD_WIDTH;
  // Transform carousel data to card data

  const totalCards = carouselData.cards.length;
  const maxShift = Math.max(0, totalCards - VISIBLE_CARDS);

  // Handle keyboard navigation for cards
  const handleCardKeyDown = (event: React.KeyboardEvent, cardIndex: number) => {
    const visibleStartIndex = startIndex;
    const visibleEndIndex = startIndex + VISIBLE_CARDS - 1;
    const isFirstVisible = cardIndex === visibleStartIndex;
    const isLastVisible = cardIndex === Math.min(visibleEndIndex, totalCards - 1);

    if (event.key === 'Tab') {
      if (!event.shiftKey && isLastVisible) {
        // Tab from last visible card - focus next button if available, otherwise let focus move naturally
        if (startIndex < maxShift && totalCards > VISIBLE_CARDS) {
          event.preventDefault();
          nextButtonRef.current?.focus();
        }
        // If no next button, let focus move to next section naturally
      } else if (event.shiftKey && isFirstVisible) {
        // Shift+Tab from first visible card - focus prev button if available, otherwise let focus move naturally
        if (startIndex > 0 && totalCards > VISIBLE_CARDS) {
          event.preventDefault();
          prevButtonRef.current?.focus();
        }
        // If no prev button, let focus move to previous section naturally
      }
    }
  };

  // Handle keyboard navigation for navigation buttons
  const handleNavButtonKeyDown = (event: React.KeyboardEvent, buttonType: 'prev' | 'next') => {
    if (event.key === 'Tab') {
      if (buttonType === 'prev' && !event.shiftKey) {
        // Tab from prev button - focus first visible card
        event.preventDefault();
        focusCardByIndex(startIndex);
      } else if (buttonType === 'next' && event.shiftKey) {
        // Shift+Tab from next button - focus last visible card
        event.preventDefault();
        const lastVisibleIndex = Math.min(startIndex + VISIBLE_CARDS - 1, totalCards - 1);
        focusCardByIndex(lastVisibleIndex);
      }
    }
  };

  const handleNext = () => {
    setDragOffset(0); // reset any drag remainder
    setStartIndex((prevIndex) => {
      const newIndex = prevIndex >= maxShift ? 0 : prevIndex + 1;
      requestAnimationFrame(() => {
        setTimeout(() => {
          let targetCardIndex;
          if (newIndex === 0) {
            targetCardIndex = 0;
          } else {
            targetCardIndex = Math.min(newIndex + VISIBLE_CARDS - 1, totalCards - 1);
          }
          const targetCard = cardRefs.current[targetCardIndex];
          if (targetCard) {
            targetCard.setAttribute('aria-live', 'assertive');
            targetCard.focus();
            setTimeout(() => {
              targetCard.setAttribute('aria-live', 'off');
            }, 100);
          }
        }, 50);
      });
      return newIndex;
    });
  };

  const handlePrev = () => {
    setDragOffset(0); // reset any drag remainder
    setStartIndex((prevIndex) => {
      const newIndex = prevIndex === 0 ? maxShift : prevIndex - 1;
      requestAnimationFrame(() => {
        setTimeout(() => {
          const targetCard = cardRefs.current[newIndex];
          if (targetCard) {
            targetCard.setAttribute('aria-live', 'assertive');
            targetCard.focus();
            setTimeout(() => {
              targetCard.setAttribute('aria-live', 'off');
            }, 100);
          }
        }, 50);
      });
      return newIndex;
    });
  };

  const focusCardByIndex = (cardIndex: number) => {
    const cardElement = cardRefs.current[cardIndex];
    if (cardElement) {
      // Force blur first, then focus to ensure screen reader announces correctly
      if (document.activeElement) {
        (document.activeElement as HTMLElement).blur();
      }
      setTimeout(() => {
        cardElement.focus();
      }, 50);
    }
  };

  // const transformValue = useMemo(() => {
  //   const cardOffset = CARD_WIDTH + CARD_MARGIN;
  //   return `translateX(-${startIndex * cardOffset}px)`;
  // }, [startIndex]);

  // Calculate viewport width to show exactly 3 cards + 0.25 of the 4th card
  const viewportWidth =
    VISIBLE_CARDS * (cardWidth) + 2*32;

  const cardOffset = cardWidth + 32;
  const canDrag = totalCards > VISIBLE_CARDS;
  const dragThreshold = cardOffset * 0.15; // need to drag 25% of a card

  const onPointerDown = (e: React.PointerEvent) => {
    if (!canDrag) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging || dragStartX === null) return;
    const delta = e.clientX - dragStartX;

    // Clamp when at boundaries
    if (startIndex === 0 && delta > 0) {
      setDragOffset(delta * 0.35); // resistance
    } else if (startIndex === maxShift && delta < 0) {
      setDragOffset(delta * 0.35);
    } else {
      setDragOffset(delta);
    }
  };

  const endDrag = (e: React.PointerEvent) => {
    if (!isDragging) return;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    setIsDragging(false);

    if (dragOffset <= -dragThreshold) {
      handleNext();
    } else if (dragOffset >= dragThreshold) {
      handlePrev();
    } else {
      // Snap back
      setDragOffset(0);
    }
    setDragStartX(null);
  };

  return (
    <Box component="section" aria-labelledby={headingId} sx={{ width: '100%', py: 2 }}>
      {/* Heading aligned with container */}
      <Container
        maxWidth="lg"
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 4, gap: "16px" }}
      disableGutters>
        <Typography
          component="h2"
          id={headingId}
          variant="h3"
          sx={{
            // fontFamily: 'Open Sans',
            fontWeight: '700 !important',
            fontSize: '24px !important',
            lineHeight: '36px'
          }}
        >
          {carouselData.header}
        </Typography>
        <Typography
          variant='body1'
          sx={{
            fontSize: '16px'
          }}
        >
          {carouselData.body}
        </Typography>
      </Container>
      {/* Carousel extends beyond container */}
      <Box sx={{ position: 'relative' }}>
        <Container maxWidth="lg" sx={{ position: 'relative', maxWidth:`${viewportWidth}`, marginLeft:'0px', marginRight:'0px' }} disableGutters>
          <Box
            ref={carouselRef}
            role="region"
          >
            <Box
              component="ul"
              className='card-grid'
              
              sx={{
                display: 'flex',
                transition: isDragging ? 'none' : 'transform 0.5s ease-in-out',
                transform: `translateX(calc(-${startIndex * cardOffset}px + ${dragOffset}px))`,
                width: 'fit-content',
                padding: 0,
                margin: 0,
                gap:'32px',
                listStyle: 'none',
                userSelect: 'none',
                touchAction: 'pan-y'
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              {carouselData?.cards?.map((card, index) => {
                const isFullyVisible = index >= startIndex && index < startIndex + VISIBLE_CARDS;
                return (
                  <FeatureCard
                    key={card.id}
                    {...card}
                    cardWidth={cardWidth}
                    ref={(el) => { cardRefs.current[index] = el; }}
                    isFirst={index === 0}
                    headingId={headingId}
                    tabIndex={isFullyVisible ? 0 : -1}
                    aria-hidden={!isFullyVisible}
                    onKeyDown={(event) => handleCardKeyDown(event, index)}
                    position={`item ${index + 1}`}
                    total={totalCards}
                  />
                );
              })}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

const ConnectionOptionsCarousel = () => {
  const { t } = useTranslation();
  const data: any = t('ndc-program.home.connectionOptions', { returnObjects: true });
  return (
    <Box sx={{ width: '100%' }}>
      <SingleCarousel carouselData={data} />
    </Box>
  );
};

export default ConnectionOptionsCarousel;